"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Leaf, ArrowRight, CircleCheck } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="147716" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">Analysis Requested</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8 border-secondary text-secondary hover:bg-secondary/5">
                    Submit Another Property
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Owner Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" className="bg-background/50 border-border/50 focus-visible:ring-secondary focus-visible:border-secondary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Enter your full name or entity name.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus-visible:ring-secondary focus-visible:border-secondary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">We'll send your analysis here.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(407) 555-0123" className="bg-background/50 border-border/50 focus-visible:ring-secondary focus-visible:border-secondary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Optional – for a faster consultation.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="project_description" rules={{
      required: "Property details are required",
      minLength: {
        value: 10,
        message: "Please provide the address (min 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Property Address & Details</FormLabel>
                        <FormControl>
                            <Textarea placeholder="123 Orlando Ave, Orlando, FL... Tell us about your property." className="bg-background/50 border-border/50 focus-visible:ring-secondary focus-visible:border-secondary min-h-[120px] rounded-xl resize-none transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Include beds/baths, square footage, and current condition.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Expected Monthly Rent (USD)</FormLabel>
                        <FormControl>
                            <Input type="number" placeholder="2500" className="bg-background/50 border-border/50 focus-visible:ring-secondary focus-visible:border-secondary h-12 rounded-xl transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Optional – helps us benchmark your ROI.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-start gap-3">
                    <span className="mt-0.5"></span>
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-secondary/30 hover:shadow-secondary/50 transition-all duration-300 group">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing Request...
                    </> : <>
                        Get Rental Analysis
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}
export default function Inquiry() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return;
}